/**
 * main.ts — Boot sequence for ChoiceRealms: Gallowcrest.
 *
 * Initializes all UI services and Phase 3 systems, creates the GameEngine,
 * and starts the game with the TitleScene.
 */

import { Terminal } from '@ui/Terminal';
import { InputManager } from '@ui/InputManager';
import { MenuSystem } from '@ui/MenuSystem';
import { StatusBar } from '@ui/StatusBar';
import { AsciiArtRenderer } from '@ui/AsciiArt';
import { TextEffects } from '@ui/TextEffects';
import { ScreenLayout } from '@ui/ScreenLayout';
import { ViewportManager } from '@ui/ViewportManager';
import { InputPrompt } from '@ui/InputPrompt';
import { NarrativeRenderer } from '@ui/NarrativeRenderer';
import { StatCheckRenderer } from '@ui/StatCheckRenderer';
import { EventSystem } from '@core/engine/EventSystem';
import { SeededRNG, NameGenerator } from '@core/engine/RandomGen';
import { GameEngine, type GameContext } from '@core/engine/GameEngine';
import { TitleScene } from '@game/scenes/TitleScene';
import { theme } from '@ui/ColorTheme';

// Visual effects
import { CRTEffect } from '@ui/CRTEffect';
import { ParticleSystem } from '@ui/ParticleSystem';
import { VisualLayer } from '@ui/VisualLayer';
import { TerminalFrame } from '@ui/TerminalFrame';
import { HintManager } from '@ui/HintManager';
import { SpriteRenderer } from '@ui/SpriteRenderer';
import { CanvasPixelRenderer } from '@ui/CanvasPixelRenderer';
import { FrameRenderer } from '@ui/FrameRenderer';
import { TransitionManager } from '@ui/TransitionManager';
import { NotificationManager } from '@ui/AchievementNotifier';
import { TooltipSystem } from '@ui/TooltipSystem';
import { ProgressIndicator } from '@ui/ProgressIndicator';
import { ModalManager } from '@ui/ModalManager';
import { InputWatchdog } from '@ui/InputWatchdog';
import { GALLOWCREST_HINTS } from '@game/data/hints';

// Phase 3 systems
import { StoryEngine } from '@core/systems/StoryEngine';
import { SkillCheckSystem } from '@core/systems/SkillCheckSystem';
import { CharacterSystem } from '@core/systems/CharacterSystem';
import { SaveLoadSystem } from '@core/engine/SaveLoad';
import { SettingsManager } from '@game/Settings';
import { AbilitySystem } from '@core/systems/AbilitySystem';
import { AchievementSystem } from '@core/systems/AchievementSystem';
import { CombatSystem } from '@core/systems/CombatSystem';
import { SkillSystem } from '@core/systems/SkillSystem';
import { TravelSystem } from '@core/systems/TravelSystem';
import { FactionSystem } from '@core/systems/FactionSystem';
import { buildNodeIndex } from '@game/data/chapterLoader';
import { analytics } from '@game/Analytics';
import { getAchievement } from '@game/data/achievements';

// --- Boot ---

async function boot(): Promise<void> {
  performance.mark('boot-start');

  const container = document.getElementById('terminal-container');
  if (!container) {
    throw new Error('Terminal container #terminal-container not found');
  }

  // Initialize UI services
  const terminal = new Terminal(container);
  const input = new InputManager(terminal);
  const menu = new MenuSystem(terminal, input);
  const statusBar = new StatusBar(terminal);
  const art = new AsciiArtRenderer(terminal);
  const effects = new TextEffects(terminal, input);
  const transitions = new TransitionManager(terminal, effects);
  const layout = new ScreenLayout(terminal);
  const viewport = new ViewportManager(terminal);
  const events = new EventSystem();
  const prompt = new InputPrompt(terminal, input, menu, effects);
  const narrative = new NarrativeRenderer(terminal, input);
  const statCheckRenderer = new StatCheckRenderer(terminal, input);

  // Initialize RNG and name generator
  const rng = new SeededRNG();
  const names = new NameGenerator(rng);

  // Initialize Phase 3 systems
  const storyEngine = new StoryEngine(null, events, buildNodeIndex);
  const skillCheck = new SkillCheckSystem();
  const characterSystem = new CharacterSystem(events);
  const saveSystem = new SaveLoadSystem();
  const settings = new SettingsManager();
  const abilitySystem = new AbilitySystem(events);
  const achievementSystem = new AchievementSystem(events);
  const combatSystem = new CombatSystem(events);
  const skillSystem = new SkillSystem();
  const travelSystem = new TravelSystem(events);
  const factionSystem = new FactionSystem(events);
  skillCheck.setSkillSystem(skillSystem);
  combatSystem.setSkillSystem(skillSystem);

  // Initialize visual effects
  const crt = new CRTEffect(container);
  const particles = new ParticleSystem(container);
  const visualLayer = new VisualLayer(container, crt, particles, settings);
  const terminalFrame = new TerminalFrame(container);

  // Initialize UI enhancement modules
  const hintManager = new HintManager({ terminal, effects });
  hintManager.registerHints(GALLOWCREST_HINTS);

  // Register sprite assets (must be done before creating SpriteRenderer instance)
  SpriteRenderer.registerSprites({
    // Characters/Classes
    frostblade: '/assets/sprites/frostblade.png',
    necromancer_hunter: '/assets/sprites/necromancer_hunter.png',
    dragon_knight: '/assets/sprites/dragon_knight.png',
    winter_witch: '/assets/sprites/winter_witch.png',
    runepriest: '/assets/sprites/runepriest.png',

    // Locations
    gallowcrest_citadel: '/assets/sprites/gallowcrest_citadel.png',
    frozen_wastes: '/assets/sprites/frozen_wastes.png',
    ancient_library: '/assets/sprites/ancient_library.png',
    shadow_keep: '/assets/sprites/shadow_keep.png',
    dragons_lair: '/assets/sprites/dragons_lair.png',
    crystal_caves: '/assets/sprites/crystal_caves.png',
    temple_of_dawn: '/assets/sprites/temple_of_dawn.png',
    the_forge: '/assets/sprites/the_forge.png',

    // Enemies/Bosses
    undead_warrior: '/assets/sprites/undead_warrior.png',
    frost_wyrm: '/assets/sprites/frost_wyrm.png',
    shadow_lord: '/assets/sprites/shadow_lord.png',
    ancient_guardian: '/assets/sprites/ancient_guardian.png',
    lich_king: '/assets/sprites/lich_king.png',

    // Items/UI
    frostcrown: '/assets/sprites/frostcrown.png',
    prophecy_scroll: '/assets/sprites/prophecy_scroll.png',
    achievement_medal: '/assets/sprites/achievement_medal.png',
    runesword: '/assets/sprites/runesword.png',
  });

  const sprites = new SpriteRenderer(terminal);
  const canvasArt = new CanvasPixelRenderer(terminal);
  const frameRenderer = new FrameRenderer(terminal);
  frameRenderer.setTerminalFrame(terminalFrame);
  const notifier = new NotificationManager();
  notifier.setTerminal(terminal);
  const tooltips = new TooltipSystem({ terminal, inputManager: input });
  const modalManager = new ModalManager({ tooltips, inputManager: input });
  const watchdog = new InputWatchdog(terminal, input);
  input.setWatchdog(watchdog);
  const progressIndicator = new ProgressIndicator();

  // Apply saved accessibility settings to theme singleton
  const savedSettings = settings.get();
  theme.setColorblindMode(savedSettings.colorblindMode);
  theme.setHighContrastMode(savedSettings.highContrast);
  terminal.setFontSize(savedSettings.fontSize);

  // Wire settings to TextEffects for consistent typewriter speed
  effects.setSettings(settings);

  // Apply saved visual effect settings via VisualLayer
  visualLayer.applySettings();
  visualLayer.setNotifier(notifier);
  if (savedSettings.terminalFrame) terminalFrame.enable();

  // Wire achievement unlock notifications
  events.on('achievementUnlocked', (data: { id: string }) => {
    const achievement = getAchievement(data.id);
    if (achievement) {
      notifier.showAchievement(achievement);
    }
  });

  // Build the game context
  const context: GameContext = {
    terminal,
    input,
    menu,
    statusBar,
    art,
    effects,
    transitions,
    layout,
    viewport,
    events,
    prompt,
    narrative,
    statCheckRenderer,
    rng,
    names,
    gameState: null,
    storyEngine,
    skillCheck,
    characterSystem,
    saveSystem,
    settings,
    abilitySystem,
    achievementSystem,
    combatSystem,
    skillSystem,
    travelSystem,
    factionSystem,
    crt,
    particles,
    visualLayer,
    terminalFrame,
    hintManager,
    sprites,
    canvasArt,
    frameRenderer,
    notifier,
    tooltips,
    modalManager,
    progressIndicator,
  };

  // Create and start the engine
  const engine = new GameEngine(context);

  // Wire engine reference for escape menu (save/quit)
  effects.setEngine(engine);

  // Wire watchdog recovery to reset to title screen on stuck state
  watchdog.setRecoveryCallback(() => {
    engine.resetTo(new TitleScene());
  });

  // Expose build info for QA verification
  window.__GALLOWCREST_BUILD__ = {
    version: __BUILD_VERSION__,
    timestamp: __BUILD_TIMESTAMP__,
    id: __BUILD_ID__,
  };
  console.log(`🎮 Gallowcrest v${__BUILD_VERSION__} (${__BUILD_ID__}) — Built ${new Date(__BUILD_TIMESTAMP__).toLocaleString()}`);

  // Start FPS monitoring for auto-quality scaling
  visualLayer.startMonitoring();

  const titleScene = new TitleScene();

  // Auto-focus terminal so keyboard works immediately
  terminal.focus();

  analytics.sessionStart();
  await engine.start(titleScene);

  performance.mark('boot-end');
  const bootMeasure = performance.measure('boot', 'boot-start', 'boot-end');
  console.log(`Boot time: ${bootMeasure.duration.toFixed(1)}ms`);
}

// --- Entry Point ---

boot().catch((error) => {
  console.error('Failed to boot ChoiceRealms: Gallowcrest', error);
  document.body.innerHTML = `
    <div style="color: #ccc; padding: 40px; font-family: 'Cascadia Mono', 'Fira Code', 'Consolas', monospace; background: #000; min-height: 100vh;">
      <h1 style="color: #ff6666;">Gallowcrest</h1>
      <p style="color: #888;">Something went wrong during startup.</p>
      <p style="color: #fff; margin-top: 16px;">${error.message ?? 'Unknown error'}</p>
      <p style="color: #888; margin-top: 24px;">Try refreshing the page. If the problem persists, clear your browser data for this site.</p>
      <button onclick="location.reload()" style="margin-top: 16px; padding: 8px 24px; background: #333; color: #fff; border: 1px solid #666; cursor: pointer; font-family: inherit; font-size: 14px;">
        Refresh
      </button>
    </div>
  `;
});
